import React, { Suspense, lazy } from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { IntlProviderWrapper } from "./utility/context/Internationalization";
import { Layout } from "./utility/context/Layout";
import * as serviceWorker from "./serviceWorker";
import Spinner from "./components/@vuexy/spinner/Fallback-spinner";
import {
  AUTH_STORAGE_KEYS,
  clearClientSession,
} from "./utility/sessionCleanup";
import { migrateWebDispatcherStorage } from "./utility/storageMigration";
import { safeGetStorageItem, safeSetStorageItem } from "./utility/safeStorage";
import { AUTH_V2_ENDPOINTS } from "./utility/authEndpoints";
import "./index.scss";
import "./i18n";

const LazyApp = lazy(() => import("./App"));
const BUILD_VERSION_STORAGE_KEY = "webDispatcherBuildVersion";
const BUILD_VERSION = process.env.REACT_APP_BUILD_VERSION || "development";
const defaultAppConfig = {
  REACT_APP_APP_ROLE: "dispatcher",
  REACT_APP_REFRESH_UNIT_TIME: 5000,
  REACT_APP_API_URL: "",
  REACT_APP_REGION: "",
};

const isResizeObserverLoopError = (message) =>
  typeof message === "string" &&
  (message.includes("ResizeObserver loop completed with undelivered notifications") ||
    message.includes("ResizeObserver loop limit exceeded"));

const initializeResizeObserverErrorGuard = () => {
  if (typeof window === "undefined" || window.__resizeObserverErrorGuardInitialized) {
    return;
  }

  window.__resizeObserverErrorGuardInitialized = true;

  window.addEventListener(
    "error",
    (event) => {
      if (isResizeObserverLoopError(event?.message)) {
        event.stopImmediatePropagation();
      }
    },
    true,
  );
};

const resetAuthForNewBuild = async () => {
  const previousBuildVersion = safeGetStorageItem(BUILD_VERSION_STORAGE_KEY);
  const hasStoredAuth = AUTH_STORAGE_KEYS.some((key) => safeGetStorageItem(key));

  if (previousBuildVersion === BUILD_VERSION) {
    return;
  }

  if (hasStoredAuth) {
    const token = safeGetStorageItem("token");
    const logoutController = new AbortController();
    const logoutTimeout = window.setTimeout(() => logoutController.abort(), 1500);

    try {
      await fetch(AUTH_V2_ENDPOINTS.logout, {
        method: "POST",
        credentials: "same-origin",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        signal: logoutController.signal,
      });
    } catch (error) {
      console.warn("Nepodařilo se odhlásit starou session po nasazení nové verze:", error);
    } finally {
      window.clearTimeout(logoutTimeout);
    }
  }

  clearClientSession();
  safeSetStorageItem(BUILD_VERSION_STORAGE_KEY, BUILD_VERSION);
};

const updateViewportCssVars = () => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  const viewportHeight =
    window.visualViewport?.height ||
    window.innerHeight ||
    document.documentElement.clientHeight ||
    0;
  const viewportWidth =
    window.visualViewport?.width ||
    window.innerWidth ||
    document.documentElement.clientWidth ||
    0;

  document.documentElement.style.setProperty("--app-vh", `${viewportHeight * 0.01}px`);
  document.documentElement.style.setProperty("--app-vw", `${viewportWidth * 0.01}px`);
  document.documentElement.style.setProperty("--app-dvh", `${viewportHeight}px`);
  document.documentElement.style.setProperty("--vh", `${viewportHeight * 0.01}px`);
};

const initializeViewportCssVars = () => {
  if (typeof window === "undefined") {
    return;
  }

  updateViewportCssVars();

  if (window.__appViewportListenersInitialized) {
    return;
  }

  window.__appViewportListenersInitialized = true;

  const handleViewportChange = () => {
    window.requestAnimationFrame(updateViewportCssVars);
  };

  window.addEventListener("resize", handleViewportChange, { passive: true });
  window.addEventListener("orientationchange", handleViewportChange, {
    passive: true,
  });
  window.addEventListener("pageshow", handleViewportChange, { passive: true });

  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", handleViewportChange, {
      passive: true,
    });
    window.visualViewport.addEventListener("scroll", handleViewportChange, {
      passive: true,
    });
  }
};

const renderApp = () => {
  const { store } = require("./redux/storeConfig/store");

  ReactDOM.render(
    <Provider store={store}>
      <Suspense fallback={<Spinner />}>
        <Layout>
          <IntlProviderWrapper>
            <LazyApp />
          </IntlProviderWrapper>
        </Layout>
      </Suspense>
    </Provider>,
    document.getElementById("root")
  );

  // Preferujeme vždy čerstvý build místo agresivního cache chování v Chromu.
  serviceWorker.unregister();
};

const configUrl = `${process.env.PUBLIC_URL || ""}/config.json`;

initializeResizeObserverErrorGuard();
initializeViewportCssVars();
migrateWebDispatcherStorage();

resetAuthForNewBuild()
  .then(() => fetch(configUrl, { cache: "no-store" }))
  .then((response) => {
    if (!response.ok) {
      throw new Error(`Config request failed with status ${response.status}`);
    }

    return response.json();
  })
  .then((config) => {
    window.appConfig = {
      ...defaultAppConfig,
      ...(config || {}),
    };
    renderApp();
  })
  .catch((error) => {
    console.error("Chyba při načítání konfigurace:", error);
    window.appConfig = defaultAppConfig;
    renderApp();
  });
